import { deepEqual } from '@casa/util';
import { TILE_OPTIONS_SCHEMAS, tileOptionsJsonSchema, type ScreenTile, type StylePreset } from './dashboard.ts';

// Styles to pick for a tile (tile-styles.md): each for one tile type, a tile style and that type's
// look options (TY-1, TY-2), applied by copying them over the tile's own (TY-3, TY-8). The look
// options are those marked `x-look` in the options' schemas, so that a new tile type needs no list
// here.

type TileType = keyof typeof TILE_OPTIONS_SCHEMAS;

/** A built-in style: an id for `styleName` (TY-8), beside its name. */
export interface BuiltInStyle extends StylePreset {
    readonly id: string;
}

/** A look option of a tile type: where it is in the options, and its default, where it has one. */
export interface LookOption {
    readonly path: readonly string[];
    readonly default?: unknown;
}

/** An optional part of a tile type a style may add or take away (TY-2): where it is, and what a new one starts as. */
export interface StylePart {
    readonly path: readonly string[];
    readonly start: Readonly<Record<string, unknown>>;
}

const isObject = (value: unknown) : value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const marksCache = new Map<TileType, { looks: LookOption[]; parts: StylePart[] }>();

/**
 * The look options and parts of a tile type (TY-2), from the `x-look` and `x-part` marks in its
 * options' schema: in sub-objects too (a gauge's, a sparkline's), and in each way of a type set up
 * two ways (a thermostat's), each once. Not in lists.
 */
function marksOf(type: TileType) : { looks: LookOption[]; parts: StylePart[] } {
    const cached = marksCache.get(type);
    if (cached) {
        return cached;
    }

    const looks = new Map<string, LookOption>();
    const parts = new Map<string, StylePart>();
    const walk = (schema: unknown, path: string[]) : void => {
        if (!isObject(schema)) {
            return;
        }
        if (schema['x-look'] === true) {
            looks.set(path.join('.'), 'default' in schema ? { path, default: schema.default } : { path });
            return;
        }
        if (isObject(schema['x-part'])) {
            parts.set(path.join('.'), { path, start: schema['x-part'] });
        }
        for (const [name, property] of Object.entries(isObject(schema.properties) ? schema.properties : {})) {
            walk(property, [...path, name]);
        }
        for (const variant of [...(Array.isArray(schema.anyOf) ? schema.anyOf : []), ...(Array.isArray(schema.oneOf) ? schema.oneOf : [])]) {
            walk(variant, path);
        }
    };
    walk(tileOptionsJsonSchema(type), []);

    const marks = { looks: [...looks.values()], parts: [...parts.values()] };
    marksCache.set(type, marks);

    return marks;
}

/** The look options of a tile type (TY-2). */
export function lookOptions(type: TileType) : LookOption[] {
    return marksOf(type).looks;
}

/** The parts of a tile type a style may add or take away (TY-2): a value tile's gauge and sparkline. */
export function styleParts(type: TileType) : StylePart[] {
    return marksOf(type).parts;
}

/** The value at `path` in `root`; undefined where a step is missing. */
function valueAt(root: unknown, path: readonly string[]) : unknown {
    let value = root;
    for (const step of path) {
        if (!isObject(value)) {
            return undefined;
        }
        value = value[step];
    }

    return value;
}

/** The object holding the value at `path` in `root`, or undefined where the tile has no such part (no gauge, no sparkline). */
function parentAt(root: Record<string, unknown>, path: readonly string[]) : Record<string, unknown> | undefined {
    const parent = valueAt(root, path.slice(0, -1));
    return path.length === 1 ? root : isObject(parent) ? parent : undefined;
}

/** A copy of JSON data (options and styles are). */
const copy = <T>(value: T) : T => JSON.parse(JSON.stringify(value)) as T;

/** Whether `tile` has a look of its own that a style would replace (TY-3): a style, or a look option off its default. */
export function hasOwnLook(tile: ScreenTile) : boolean {
    if (tile.style !== undefined && Object.keys(tile.style).length > 0) {
        return true;
    }
    if (tile.type === 'group') {
        return false;
    }

    // Not those of a part the tile lacks (no gauge, no sparkline).
    const options = tile.options as Record<string, unknown>;
    return lookOptions(tile.type).some((option) => parentAt(options, option.path) !== undefined && !deepEqual(valueAt(options, option.path), option.default));
}

/**
 * The look options `tile` sets off their defaults, as they nest, for saving as a style (TY-9), and
 * for each part, whether it has it: with what a new one starts as (a gauge's range), or `false`.
 * Empty for a group.
 */
export function lookOf(tile: ScreenTile) : Record<string, unknown> {
    const look: Record<string, unknown> = {};
    if (tile.type === 'group') {
        return look;
    }

    for (const option of lookOptions(tile.type)) {
        const value = valueAt(tile.options, option.path);
        if (value === undefined || deepEqual(value, option.default)) {
            continue;
        }

        let parent = look;
        for (const step of option.path.slice(0, -1)) {
            const next = isObject(parent[step]) ? (parent[step] as Record<string, unknown>) : {};
            parent[step] = next;
            parent = next;
        }
        parent[option.path[option.path.length - 1] as string] = value;
    }
    for (const part of styleParts(tile.type)) {
        const key = part.path[part.path.length - 1] as string;
        const parent = parentAt(look, part.path) ?? look;
        const own = valueAt(tile.options, part.path);
        if (!isObject(own)) {
            parent[key] = false;
            continue;
        }
        const start = Object.fromEntries(Object.keys(part.start).filter((name) => own[name] !== undefined).map((name) => [name, own[name]]));
        parent[key] = { ...start, ...(isObject(parent[key]) ? parent[key] : {}) };
    }

    return look;
}

/**
 * `options` with the parts `look` says added or taken away (`false`), and its look options set to
 * `look`'s values, else their defaults, else left out. A part `look` says nothing of stays as it
 * is; one it adds starts as the part does, or with the style's values for those keys (a gauge's
 * range).
 */
function withLook(type: TileType, options: Record<string, unknown>, look: unknown) : Record<string, unknown> {
    const result = copy(options);
    for (const part of styleParts(type)) {
        const parent = parentAt(result, part.path);
        const wanted = valueAt(look, part.path);
        const key = part.path[part.path.length - 1] as string;
        if (!parent) {
            continue;
        }
        if (wanted === false) {
            delete parent[key];
        } else if (isObject(wanted) && !isObject(parent[key])) {
            parent[key] = Object.fromEntries(Object.entries(part.start).map(([name, start]) => [name, copy(wanted[name] ?? start)]));
        }
    }
    for (const option of lookOptions(type)) {
        const parent = parentAt(result, option.path);
        if (!parent) {
            continue;
        }

        const key = option.path[option.path.length - 1] as string;
        const value = valueAt(look, option.path);
        if (value !== undefined) {
            parent[key] = copy(value);
        } else if ('default' in option) {
            parent[key] = copy(option.default);
        } else {
            delete parent[key];
        }
    }

    return result;
}

/**
 * `tile` with `preset` applied (TY-3): its style replaced by the style's, and its look options set
 * to the style's values, or to their defaults. The style must be one for the tile's type. `undefined` is "The dashboard's style"
 * (TY-4): no style of its own, every look option at its default. The tile's other options, its
 * style rules among them (TY-7), are kept. Look values that do not suit the tile's options (a
 * user's style from an older version, say) are not used: the defaults are, as for a style without
 * them.
 */
export function applyStylePreset<T extends ScreenTile>(tile: T, preset: (StylePreset & { readonly id?: string }) | undefined) : T {
    if (preset && preset.type !== tile.type) {
        throw new Error(`The style "${preset.name}" is for a ${preset.type} tile, not a ${tile.type} tile`);
    }

    const { style: _style, styleName: _styleName, ...rest } = tile;
    const applied: Record<string, unknown> = { ...rest };
    if (preset) {
        if (Object.keys(preset.style).length > 0) {
            applied.style = copy(preset.style);
        }
        applied.styleName = preset.id ?? preset.name;
    }
    if (tile.type === 'group') {
        return applied as T;
    }

    const options = tile.options as Record<string, unknown>;
    // As parsed: a part added gets its defaults (a gauge's further values).
    const looked = TILE_OPTIONS_SCHEMAS[tile.type].safeParse(withLook(tile.type, options, preset?.options));
    applied.options = looked.success ? looked.data : withLook(tile.type, options, undefined);

    return applied as T;
}

/** The parts `tile` has that `preset` takes away (TY-2), by name: `gauge`, `sparkline`. */
export function partsRemovedBy(tile: ScreenTile, preset: StylePreset | undefined) : string[] {
    if (tile.type === 'group' || !preset) {
        return [];
    }

    return styleParts(tile.type)
        .filter((part) => valueAt(preset.options, part.path) === false && isObject(valueAt(tile.options, part.path)))
        .map((part) => part.path.join('.'));
}

/**
 * The style `tile` was given (its `styleName`), among `presets` for its type, while its style and
 * look are still that style's (TY-6, TY-8); undefined once they changed, or where it was given none.
 */
export function givenStylePreset<P extends StylePreset & { readonly id?: string }>(tile: ScreenTile, presets: readonly P[]) : P | undefined {
    const preset = tile.styleName === undefined ? undefined : presets.find((each) => each.type === tile.type && (each.id ?? each.name) === tile.styleName);

    return preset && deepEqual(applyStylePreset(tile, preset), tile) ? preset : undefined;
}

/**
 * A style of the user's own made from `tile` (TY-9): its style and its look options, for its type,
 * with `shape` where it is made for one (TY-10). Applied to the tile, it changes nothing.
 */
export function styleOfTile(tile: Exclude<ScreenTile, { type: 'group' }>, name: string, shape?: StylePreset['shape']) : StylePreset {
    return { name, type: tile.type, ...(shape ? { shape } : {}), style: copy(tile.style ?? {}), options: lookOf(tile) };
}

/** Whether `name` is taken by a built-in style for `type`, by its name or its id, in any case (TY-9): a style of the user's own may not have it. */
export function isBuiltInStyleName(type: ScreenTile['type'], name: string) : boolean {
    const wanted = name.trim().toLowerCase();

    return BUILT_IN_STYLES.some((style) => style.type === type && (style.name.toLowerCase() === wanted || style.id === wanted));
}

/**
 * The styles offered for a tile of `type` (TY-1): the built-in ones for it, then the dashboard's own
 * (TY-9). None for a group, or for a type no style is for: then none is offered.
 */
export function stylePresetsFor(type: ScreenTile['type'], own: readonly StylePreset[] = []) : (StylePreset & { readonly id?: string })[] {
    return [...BUILT_IN_STYLES, ...own].filter((preset) => preset.type === type);
}

/**
 * The built-in styles (TY-4), after "The dashboard's style", which is no style (`undefined`): for
 * value tiles only, so far (the owner, 2026-10-29). The colours suit the dark tiles; none sets look
 * options yet, so each puts a tile's back to their defaults. Those without a shape suit any (TY-10).
 */
export const BUILT_IN_STYLES: readonly BuiltInStyle[] = [
    {
        id: 'classic',
        name: 'Classic',
        type: 'value',
        // The legacy tile, as the defaults are (TS-1), set explicitly so that a dashboard's style does not change it.
        style: { background: '#151515', border: 'none', radius: 4, padding: 5, titleUppercase: true, titleAlign: 'center', valueAlign: 'center', shadow: false },
        options: {},
    },
    {
        id: 'card',
        name: 'Card',
        type: 'value',
        // The owner's card of 2026-10-28 (dashboard-editor.md TS-15), a landscape one.
        shape: 'wide',
        style: {
            background: '#1a2030', border: 'none', radius: 14, padding: 12,
            titleUppercase: false, titleAlign: 'left', valueAlign: 'left',
            iconPosition: 'top-right', iconSize: 48, iconBackground: '#1e3a5f', iconRadius: 24, iconColor: '#4fb3ff',
            captionColor: '#8fb4ff',
        },
        options: {},
    },
    {
        id: 'minimal',
        name: 'Minimal',
        type: 'value',
        style: { showBackground: false, titleUppercase: false, titleAlign: 'left', titleHeight: 20, valueAlign: 'left' },
        options: {},
    },
    {
        id: 'outline',
        name: 'Outline',
        type: 'value',
        style: { background: 'none', border: '#3a3a3a', borderWidth: 1, radius: 8 },
        options: {},
    },
    {
        id: 'raised',
        name: 'Raised',
        type: 'value',
        style: { background: '#222222', border: 'none', radius: 12, padding: 8, shadow: true, shadowColor: '#80000000', shadowY: 3, shadowBlur: 8 },
        options: {},
    },
    {
        id: 'banner',
        name: 'Banner',
        type: 'value',
        shape: 'wide',
        style: { background: '#151515', radius: 6, titleBackground: '#2563eb', title: '#ffffff', titleAlign: 'left' },
        options: {},
    },
    {
        id: 'compact',
        name: 'Compact',
        type: 'value',
        style: { padding: 3, radius: 4, titleHeight: 18, titleUppercase: false, iconSize: 20 },
        options: {},
    },
    // With a gauge or a sparkline, which they add where the tile has none (TY-2).
    {
        id: 'dial',
        name: 'Dial',
        type: 'value',
        shape: 'square',
        // The legacy dial.
        style: { background: '#151515', border: 'none', radius: 4, padding: 5, titleUppercase: true, titleAlign: 'center', valueAlign: 'center', shadow: false, gaugeStyle: 'ticks' },
        options: { gauge: { colors: 'red-green' }, sparkline: false },
    },
    {
        id: 'ring',
        name: 'Ring',
        type: 'value',
        shape: 'square',
        style: { background: '#1a2030', border: 'none', radius: 14, padding: 10, titleUppercase: false, titleAlign: 'left', gaugeStyle: 'ring' },
        options: { gauge: { coloring: 'single', color: '#4fb3ff', track: '#2a3550' }, sparkline: false },
    },
    {
        id: 'arc',
        name: 'Arc',
        type: 'value',
        shape: 'square',
        style: { background: '#222222', border: 'none', radius: 12, padding: 8, titleUppercase: false, gaugeStyle: 'arc' },
        options: { gauge: { colors: 'blue-yellow-red', coloring: 'gradient' }, sparkline: false },
    },
    {
        id: 'trend',
        name: 'Trend',
        type: 'value',
        shape: 'wide',
        // A card with the day's line filled behind the value.
        style: { background: '#1a2030', border: 'none', radius: 14, padding: 12, titleUppercase: false, titleAlign: 'left', valueAlign: 'left' },
        options: { gauge: false, sparkline: { color: '#4fb3ff', fill: true, placement: 'behind' } },
    },
];
